import { useEffect, useMemo, useState, useCallback } from "react";
import { DatePicker, Input, InputNumber, Typography, Space } from "antd";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  useAddPayrollMutation,
  useGetPayslipsQuery,
} from "@/redux/rtk/features/hrm/payroll/payrollApi";
import { updatePayslip } from "@/redux/rtk/features/hrm/payroll/payrollSlice";

import PayrollTableComponent from "./PayrollTableComponent";
import Card from "@/UI/Card";
import Button from "@/UI/Button";

const { Title, Text } = Typography;

const fmtHours = (h) => {
  const n = Number(h ?? 0);
  return `${Number.isFinite(n) ? n.toFixed(2) : "0.00"} hours`;
};

export default function CalculatePayroll() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Default to current month/year
  const [pageConfig, setPageConfig] = useState({
    salaryMonth: dayjs().format("M"),
    salaryYear: dayjs().format("YYYY"),
  });

  // Selected rows (by userId)
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // RTK Query
  const { isLoading } = useGetPayslipsQuery(pageConfig);
  const [addPayslip, { isLoading: addLoading }] = useAddPayrollMutation();

  // Redux list is the working set for the table
  const list = useSelector((state) => state.payroll.list);
  const [payslips, setPayslips] = useState([]);

  useEffect(() => {
    setPayslips(list || []);
  }, [list]);

  // Handlers
  const onMonthChange = useCallback((date) => {
    setPageConfig((prev) => ({
      ...prev,
      salaryMonth: date ? dayjs(date).format("M") : undefined,
    }));
    setSelectedRowKeys([]); // reset selection on filter change
  }, []);

  const onYearChange = useCallback((date) => {
    setPageConfig((prev) => ({
      ...prev,
      salaryYear: date ? dayjs(date).format("YYYY") : undefined,
    }));
    setSelectedRowKeys([]); // reset selection on filter change
  }, []);

  const onRowSelect = useCallback((keys) => {
    setSelectedRowKeys(keys || []);
  }, []);

  // Columns (memoized)
  const columns = useMemo(
    () => [
      {
        title: "ID",
        dataIndex: "userId",
        key: "userId",
        width: 90,
      },
      {
        title: "Name",
        key: "name",
        render: (record) =>
          `${record?.firstName ?? ""} ${record?.lastName ?? ""}`.trim(),
        renderCsv: (record) =>
          `${record?.firstName ?? ""} ${record?.lastName ?? ""}`.trim(),
        ellipsis: true,
      },
      {
        title: "Salary",
        dataIndex: "salary",
        key: "salary",
        width: 120,
      },
      {
        title: "Salary Payable",
        dataIndex: "salaryPayable",
        key: "salaryPayable",
        width: 170,
        render: (salaryPayable, { userId }) => (
          <InputNumber
            size="small"
            style={{ width: 150, fontSize: 15 }}
            value={Number(salaryPayable ?? 0)}
            min={0}
            step={1}
            onChange={(value) =>
              dispatch(
                updatePayslip({ userId, value: Number(value ?? 0), key: "salaryPayable" })
              )
            }
          />
        ),
        csvOff: true,
      },
      {
        title: "Bonus",
        dataIndex: "bonus",
        key: "bonus",
        width: 130,
        render: (bonus, { userId }) => (
          <InputNumber
            size="small"
            style={{ width: 110, fontSize: 15 }}
            value={Number(bonus ?? 0)}
            min={0}
            step={1}
            onChange={(value) =>
              dispatch(updatePayslip({ userId, value: Number(value ?? 0), key: "bonus" }))
            }
          />
        ),
        csvOff: true,
      },
      {
        title: "B-Comment",
        dataIndex: "bonusComment",
        key: "bonusComment",
        width: 160,
        render: (bonusComment, { userId }) => (
          <Input
            placeholder="comment"
            size="small"
            style={{ width: 140, fontSize: 15 }}
            value={bonusComment ?? ""}
            onChange={(e) =>
              dispatch(
                updatePayslip({
                  userId,
                  value: e.target.value,
                  key: "bonusComment",
                })
              )
            }
          />
        ),
        csvOff: true,
      },
      {
        title: "Deduction",
        dataIndex: "deduction",
        key: "deduction",
        width: 130,
        render: (deduction, { userId }) => (
          <InputNumber
            size="small"
            style={{ width: 110, fontSize: 15 }}
            value={Number(deduction ?? 0)}
            min={0}
            step={1}
            onChange={(value) =>
              dispatch(
                updatePayslip({ userId, value: Number(value ?? 0), key: "deduction" })
              )
            }
          />
        ),
        csvOff: true,
      },
      {
        title: "D-Comment",
        dataIndex: "deductionComment",
        key: "deductionComment",
        width: 160,
        render: (deductionComment, { userId }) => (
          <Input
            placeholder="comment"
            size="small"
            style={{ width: 140, fontSize: 15 }}
            value={deductionComment ?? ""}
            onChange={(e) =>
              dispatch(
                updatePayslip({
                  userId,
                  value: e.target.value,
                  key: "deductionComment",
                })
              )
            }
          />
        ),
        csvOff: true,
      },
      {
        title: "Working Hours",
        dataIndex: "workingHour",
        key: "workingHour",
        width: 140,
        render: (h) => fmtHours(h),
      },
      {
        title: "Total Payable",
        dataIndex: "totalPayable",
        key: "totalPayable",
        width: 140,
      },
    ],
    [dispatch]
  );

  const handleGenerate = useCallback(async () => {
    if (!pageConfig.salaryMonth || !pageConfig.salaryYear) {
      toast.dismiss();
      toast.error("Please select month and year.");
      return;
    }

    if (selectedRowKeys.length === 0) {
      toast.dismiss();
      toast.error("Please select at least one employee to generate payslip.");
      return;
    }

    const selectedPayslipData = (payslips || []).filter((p) =>
      selectedRowKeys.includes(p.userId)
    );

    if (selectedPayslipData.length === 0) {
      toast.dismiss();
      toast.error("No matching rows found for selected employees.");
      return;
    }

    try {
      const resp = await addPayslip({
        values: selectedPayslipData,
        month: pageConfig.salaryMonth,
        year: pageConfig.salaryYear,
      }).unwrap?.();

      toast.dismiss();
      toast.success("Payslip(s) generated successfully!");
      navigate("/admin/payslip");
    } catch (error) {
      toast.dismiss();
      toast.error("Failed to generate payslip(s).");
      // console.error("Error generating payslip:", error);
    }
  }, [addPayslip, navigate, pageConfig.salaryMonth, pageConfig.salaryYear, payslips, selectedRowKeys]);

  return (
    <Card>
      {/* Header */}
      <div className="mb-4 p-3">
        <Title level={3} style={{ marginBottom: 4 }}>
          Calculate Payroll
        </Title>
        <Text type="secondary">
          Select month & year, adjust payable/bonus/deduction inline, then generate payslips.
        </Text>
      </div>

      {/* Month/Year Filters */}
      <div className="flex flex-wrap items-center gap-3 p-3">
        <Space size="middle" wrap>
          <div className="flex items-center gap-2">
            <span className="text-base text-color-2 whitespace-nowrap shrink-0">
              Select Month:
            </span>
            <DatePicker
              picker="month"
              format="MMM"
              style={{ maxWidth: 180 }}
              defaultValue={dayjs()}
              onChange={onMonthChange}
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-base text-color-2 whitespace-nowrap shrink-0">
              Select Year:
            </span>
            <DatePicker
              picker="year"
              format="YYYY"
              style={{ maxWidth: 180 }}
              defaultValue={dayjs()}
              onChange={onYearChange}
            />
          </div>
        </Space>
      </div>


      {/* Table */}
      <PayrollTableComponent
        title="Calculate Payroll"
        columns={columns}
        list={payslips}
        loading={isLoading}
        setPageConfig={setPageConfig}
        onRowSelect={onRowSelect}
        selectedRowKeys={selectedRowKeys}
        selectedRowKey="userId"
      />

      {/* Primary Action */}
      <div className="flex justify-end">
        <Button
          loading={isLoading || addLoading}
          type="primary"
          size="large"
          onClick={handleGenerate}
          className="mx-5 my-3 w-[320px] bg-[#15B983] text-white"
          disabled={!pageConfig.salaryMonth || !pageConfig.salaryYear}
        >
          Generate Payslip
        </Button>
      </div>
    </Card>
  );
}
